import React, { useState, useEffect } from 'react';
import '../SitoWeb2.css';
import { Phone, Mail, MapPin, ArrowRight, ArrowUpRight, Building2, HardHat, Ruler, PaintBucket, Shield, Clock, Menu, X, Leaf, Monitor, Cog, ChevronDown } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_colabella-tutorials/artifacts/wcwuz41r_LOGO.png';

const SitoWeb2 = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ nome: '', email: '', telefono: '', messaggio: '' });
  const [formSent, setFormSent] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };
  const handleSubmit = (e) => { e.preventDefault(); setFormSent(true); setTimeout(() => setFormSent(false), 5000); setFormData({ nome: '', email: '', telefono: '', messaggio: '' }); };

  return (
    <div className="s2">
      {/* NAV */}
      <nav className={`s2-nav ${scrolled ? 's2-nav-solid' : ''}`}>
        <div className="s2-nav-inner">
          <div className="s2-brand" onClick={() => scrollTo('top')}>
            <img src={LOGO} alt="CG" />
            <div className="s2-brand-text">
              <span className="s2-brand-main">COLABELLA</span>
              <span className="s2-brand-sub">GROUP</span>
            </div>
          </div>
          <div className={`s2-links ${menuOpen ? 's2-links-open' : ''}`}>
            <button onClick={() => scrollTo('about')}>Chi Siamo</button>
            <button onClick={() => scrollTo('values')}>Valori</button>
            <button onClick={() => scrollTo('services')}>Servizi</button>
            <button onClick={() => scrollTo('work')}>Progetti</button>
            <button onClick={() => scrollTo('contact')}>Contatti</button>
          </div>
          <a href="tel:+393202711608" className="s2-nav-cta">Parliamone <ArrowUpRight size={16} /></a>
          <button className="s2-menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X size={26} /> : <Menu size={26} />}</button>
        </div>
      </nav>

      {/* HERO */}
      <section id="top" className="s2-hero">
        <div className="s2-hero-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80)' }} />
        <div className="s2-hero-dark" />
        <div className="s2-hero-grid">
          <div className="s2-hero-left">
            <div className="s2-hero-tag">Est. 1970</div>
            <h1 className="s2-hero-h1">
              NON<br/>COSTRUIAMO<br/>EDIFICI.<br/>
              <span className="s2-hero-accent">COSTRUIAMO<br/>IL FUTURO.</span>
            </h1>
          </div>
          <div className="s2-hero-right">
            <p className="s2-hero-desc">Due generazioni. Oltre 50 anni di eccellenza. Dalla visione alla realtà, ogni progetto porta la firma Colabella.</p>
            <div className="s2-hero-actions">
              <button className="s2-btn" onClick={() => scrollTo('work')}>Vedi i Progetti <ArrowRight size={18} /></button>
              <button className="s2-btn-ghost" onClick={() => scrollTo('contact')}>Contattaci</button>
            </div>
            <div className="s2-hero-nums">
              <div><span>50+</span><small>Anni</small></div>
              <div><span>200+</span><small>Progetti</small></div>
              <div><span>100%</span><small>Passione</small></div>
            </div>
          </div>
        </div>
        <button className="s2-scroll-hint" onClick={() => scrollTo('about')}><ChevronDown size={28} /></button>
      </section>

      {/* ABOUT */}
      <section id="about" className="s2-section s2-about">
        <div className="s2-container">
          <div className="s2-about-layout">
            <div className="s2-about-left">
              <div className="s2-label">Chi Siamo</div>
              <h2 className="s2-h2">Una storia<br/>di <span className="s2-accent">famiglia.</span></h2>
            </div>
            <div className="s2-about-right">
              <p>Fondata da <strong>Leo Colabella</strong> all'inizio degli anni '70, con il coraggio di chi crede nel lavoro fatto bene. Oggi il figlio <strong>Antonio</strong> guida l'azienda con la stessa determinazione, unendo l'esperienza di oltre mezzo secolo alle tecnologie più avanzate.</p>
              <p>Non siamo una grande corporation. Siamo un'impresa che ti guarda negli occhi, che mantiene le promesse, che consegna nei tempi. <strong>Sempre.</strong></p>
            </div>
          </div>
          <div className="s2-about-images">
            <div className="s2-about-img" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80)' }} />
            <div className="s2-about-img" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80)' }} />
            <div className="s2-about-img" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80)' }} />
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section id="values" className="s2-section s2-values">
        <div className="s2-container">
          <div className="s2-label">I Nostri Pilastri</div>
          <h2 className="s2-h2">Non solo parole.<br/><span className="s2-accent">Fatti.</span></h2>
          <div className="s2-values-grid">
            {[
              { icon: Leaf, num: '01', title: 'Sostenibilità Ambientale', desc: 'Soluzioni eco-compatibili e materiali rigenerativi per un impatto urbano positivo e duraturo.' },
              { icon: Monitor, num: '02', title: 'Innovazione Digitale', desc: 'Processi digitalizzati per ottimizzare tempi, costi e precisione costruttiva in ogni cantiere.' },
              { icon: Cog, num: '03', title: 'Efficienza Operativa', desc: "Gestione diretta dell'intero ciclo, dalla concezione al completamento, con standard d'eccellenza." },
            ].map((v, i) => (
              <div key={i} className="s2-value-card">
                <div className="s2-value-top">
                  <span className="s2-value-num">{v.num}</span>
                  <v.icon size={28} />
                </div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="s2-section s2-services">
        <div className="s2-container">
          <div className="s2-label">Servizi</div>
          <h2 className="s2-h2">Dalla fondazione<br/>al <span className="s2-accent">tetto.</span></h2>
          <div className="s2-services-list">
            {[
              { icon: Building2, title: 'Costruzioni Residenziali', desc: 'Ville, appartamenti e complessi residenziali di pregio.' },
              { icon: HardHat, title: 'Ristrutturazioni', desc: 'Interventi completi rispettando tempi e budget.' },
              { icon: Ruler, title: 'Edilizia Commerciale', desc: 'Spazi commerciali, uffici e strutture industriali.' },
              { icon: PaintBucket, title: 'Finiture di Pregio', desc: 'Interior design con materiali selezionati.' },
              { icon: Shield, title: 'Sicurezza e Normativa', desc: 'Conformità totale e certificazioni complete.' },
              { icon: Clock, title: 'Project Management', desc: 'Gestione completa dalla A alla Z.' },
            ].map((s, i) => (
              <div key={i} className="s2-service-row">
                <div className="s2-service-num">{String(i + 1).padStart(2, '0')}</div>
                <s.icon size={24} className="s2-service-icon" />
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ArrowUpRight size={20} className="s2-service-arrow" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="work" className="s2-section s2-work">
        <div className="s2-container">
          <div className="s2-label">Portfolio</div>
          <h2 className="s2-h2">I nostri<br/><span className="s2-accent">lavori.</span></h2>
        </div>
        <div className="s2-work-scroll">
          {[
            { img: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=700&q=80', title: 'Villa Moderna Pescara', cat: 'Residenziale' },
            { img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=700&q=80', title: 'Residenza Lago Azzurro', cat: 'Residenziale' },
            { img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=700&q=80', title: 'Palazzo Degli Ulivi', cat: 'Residenziale' },
            { img: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=700&q=80', title: 'Centro Direzionale', cat: 'Commerciale' },
            { img: 'https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=700&q=80', title: 'Complesso Le Terrazze', cat: 'Residenziale' },
            { img: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=700&q=80', title: 'Villa Panoramica', cat: 'Residenziale' },
          ].map((p, i) => (
            <div key={i} className="s2-work-card">
              <img src={p.img} alt={p.title} />
              <div className="s2-work-info">
                <span>{p.cat}</span>
                <h3>{p.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="s2-section s2-cta">
        <div className="s2-container s2-cta-inner">
          <h2 className="s2-cta-h2">HAI UN PROGETTO<br/>IN MENTE?</h2>
          <p>Non aspettare. Ogni grande costruzione inizia con una conversazione.</p>
          <button className="s2-btn s2-btn-white" onClick={() => scrollTo('contact')}>Richiedi Preventivo <ArrowRight size={18} /></button>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="s2-section s2-contact">
        <div className="s2-container s2-contact-grid">
          <div className="s2-contact-left">
            <div className="s2-label">Contatti</div>
            <h2 className="s2-h2">Parliamo<br/>del tuo<br/><span className="s2-accent">progetto.</span></h2>
            <div className="s2-contact-items">
              <a href="tel:+393202711608"><Phone size={18} /> +39 320 271 1608</a>
              <a href="mailto:info@colabella.it"><Mail size={18} /> info@colabella.it</a>
              <span><MapPin size={18} /> Via Alba, 1 — 86046 San Martino in Pensilis (CB)</span>
            </div>
          </div>
          <form className="s2-form" onSubmit={handleSubmit}>
            <input placeholder="Nome *" required value={formData.nome} onChange={e => setFormData({...formData, nome: e.target.value})} />
            <input type="email" placeholder="Email *" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
            <input type="tel" placeholder="Telefono" value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} />
            <textarea placeholder="Il tuo progetto..." rows={4} value={formData.messaggio} onChange={e => setFormData({...formData, messaggio: e.target.value})} />
            <button type="submit" className="s2-btn s2-btn-full">{formSent ? 'Inviato!' : 'Invia Richiesta'} <ArrowRight size={18} /></button>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="s2-footer">
        <div className="s2-container s2-footer-inner">
          <div className="s2-footer-brand">
            <img src={LOGO} alt="" />
            <div><span className="s2-footer-name">COLABELLA</span><span className="s2-footer-sub">GROUP</span></div>
          </div>
          <div className="s2-footer-links">
            <button onClick={() => scrollTo('about')}>Chi Siamo</button>
            <button onClick={() => scrollTo('services')}>Servizi</button>
            <button onClick={() => scrollTo('work')}>Progetti</button>
            <button onClick={() => scrollTo('contact')}>Contatti</button>
          </div>
          <p className="s2-footer-copy">&copy; {new Date().getFullYear()} Colabella Group — San Martino in Pensilis (CB)</p>
        </div>
      </footer>
    </div>
  );
};

export default SitoWeb2;
